import { useState, useEffect, useCallback } from 'react';
import activityLogApi, { ActivityLog, ActivityType } from '../api/activityLogApi';

interface UseActivityLogFilters {
  userId?: string;
  projectId?: string;
  type?: ActivityType | ActivityType[];
  startDate?: string;
  endDate?: string;
  limit?: number;
}

interface UseActivityLogReturn {
  logs: ActivityLog[];
  loading: boolean;
  error: Error | null;
  fetchLogs: (filters?: UseActivityLogFilters) => Promise<void>;
  logActivity: (activity: Omit<ActivityLog, 'id' | 'timestamp' | 'ipAddress'>) => Promise<ActivityLog>;
}

/**
 * Custom hook for managing activity logs
 * 
 * Provides methods for fetching and creating activity logs
 */
export const useActivityLog = (initialFilters?: UseActivityLogFilters): UseActivityLogReturn => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Fetch activity logs with optional filters
  const fetchLogs = useCallback(async (filters?: UseActivityLogFilters) => {
    setLoading(true);
    setError(null);
    try {
      const data = await activityLogApi.fetchActivityLogs(filters);
      setLogs(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch activity logs'));
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Log a new activity
  const logNewActivity = useCallback(async (activity: Omit<ActivityLog, 'id' | 'timestamp' | 'ipAddress'>) => {
    try {
      const newActivity = await activityLogApi.logActivity(activity);
      // Add the new activity to the logs if it matches current filters
      setLogs(prev => [newActivity, ...prev]);
      return newActivity;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to log activity'));
      throw err;
    }
  }, []);
  
  // Fetch logs with initial filters on mount
  useEffect(() => {
    fetchLogs(initialFilters);
  }, [fetchLogs, initialFilters]);
  
  return {
    logs,
    loading,
    error,
    fetchLogs,
    logActivity: logNewActivity
  };
};

export default useActivityLog; 